import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { CurrencyDisplay } from '@/components/ui/currency-display'
import { DataTable, SortableHeader } from '@/components/ui/data-table'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  useCreateAffiliate,
  useCreateCampaign,
  useCreateReferralProgramme,
  useDeleteCampaign,
  useUpdateAffiliate,
  useUpdateCampaign,
} from '@/hooks/useAdmin'
import {
  adminQueries,
  type AdminAffiliate,
  type AdminReferralCampaign,
  type AdminReferralProgramme,
  type ReferralReward,
} from '@/services/adminApi'
import { CURRENCIES, type CurrencyCode } from '@/config/currency'

export const Route = createFileRoute('/admin/affiliates')({
  component: AdminAffiliatesPage,
})

function AdminAffiliatesPage() {
  const [programmePage, setProgrammePage] = useState(1)
  const [affiliatePage, setAffiliatePage] = useState(1)
  const [campaignPage, setCampaignPage] = useState(1)

  const programmePageSize = 20
  const affiliatePageSize = 20
  const campaignPageSize = 20

  const { data: programmesData, isLoading: isLoadingProgrammes } = useQuery(adminQueries.referralProgrammes(programmePage, programmePageSize))
  const { data: affiliatesData, isLoading: isLoadingAffiliates } = useQuery(adminQueries.activeAffiliates(affiliatePage, affiliatePageSize))
  const { data: campaignsData, isLoading: isLoadingCampaigns } = useQuery(adminQueries.campaigns(campaignPage, campaignPageSize))

  const createProgramme = useCreateReferralProgramme()
  const createAffiliate = useCreateAffiliate()
  const updateAffiliate = useUpdateAffiliate()
  const createCampaign = useCreateCampaign()
  const updateCampaign = useUpdateCampaign()
  const deleteCampaign = useDeleteCampaign()

  const [programmeRewards, setProgrammeRewards] = useState<Record<CurrencyCode, { referrer: string; referee: string }>>({
    GBP: { referrer: '500', referee: '500' },
    USD: { referrer: '700', referee: '400' },
  })

  const [selectedAffiliate, setSelectedAffiliate] = useState<AdminAffiliate | null>(null)
  const [newAffiliateAccountId, setNewAffiliateAccountId] = useState('')
  const [affiliateCode, setAffiliateCode] = useState('')
  const [affiliateRewards, setAffiliateRewards] = useState<Record<CurrencyCode, string>>({ GBP: '1000', USD: '1500' })
  const [affiliateShare, setAffiliateShare] = useState('0.15')
  const [affiliateMonths, setAffiliateMonths] = useState('6')

  const [selectedCampaign, setSelectedCampaign] = useState<AdminReferralCampaign | null>(null)
  const [campaignCode, setCampaignCode] = useState('')
  const [campaignName, setCampaignName] = useState('')
  const [campaignDescription, setCampaignDescription] = useState('')
  const [campaignRewards, setCampaignRewards] = useState<Record<CurrencyCode, string>>({ GBP: '500', USD: '400' })
  const [campaignMaxUses, setCampaignMaxUses] = useState('')
  const [campaignActiveFrom, setCampaignActiveFrom] = useState(toLocalDateTimeInputValue(new Date().toISOString()))
  const [campaignActiveUntil, setCampaignActiveUntil] = useState('')

  useEffect(() => {
    if (!selectedAffiliate) return

    setAffiliateCode(selectedAffiliate.code)
    setAffiliateRewards(rewardInputs(selectedAffiliate.rewards, 'flatFeeCents'))
    setAffiliateShare(String(selectedAffiliate.sharePercentage))
    setAffiliateMonths(String(selectedAffiliate.revenueShareMonths))
  }, [selectedAffiliate])

  useEffect(() => {
    if (!selectedCampaign) return

    setCampaignCode(selectedCampaign.code)
    setCampaignName(selectedCampaign.name)
    setCampaignDescription(selectedCampaign.description ?? '')
    setCampaignRewards(rewardInputs(selectedCampaign.rewards, 'refereeCreditCents'))
    setCampaignMaxUses(selectedCampaign.maxUses?.toString() ?? '')
    setCampaignActiveFrom(toLocalDateTimeInputValue(selectedCampaign.activeFrom))
    setCampaignActiveUntil(toLocalDateTimeInputValue(selectedCampaign.activeUntil))
  }, [selectedCampaign])

  const activeProgrammes = useMemo(
    () => (programmesData?.referralProgrammes ?? []).filter((programme) => programme.isActive),
    [programmesData?.referralProgrammes]
  )
  const activeProgramme = activeProgrammes[0] ?? null
  const affiliateItems = affiliatesData?.affiliates ?? []
  const campaignItems = campaignsData?.campaigns ?? []
  const programmeItems = programmesData?.referralProgrammes ?? []

  useEffect(() => {
    if (!activeProgramme) return

    setProgrammeRewards(programmeRewardInputs(activeProgramme.rewards))
  }, [activeProgramme])

  const resetAffiliateFields = () => {
    setAffiliateCode('')
    setAffiliateRewards({ GBP: '1000', USD: '1500' })
    setAffiliateShare('0.15')
    setAffiliateMonths('6')
  }

  const programmeColumns = useMemo<ColumnDef<AdminReferralProgramme, any>[]>(() => [
    {
      accessorKey: 'name',
      header: ({ column }) => <SortableHeader column={column} label="Name" />,
    },
    {
      id: 'rewards',
      header: 'Rewards',
      cell: ({ row }) => <RewardsSummary rewards={row.original.rewards} mode="programme" />,
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => row.original.isActive ? <Badge>Active</Badge> : <Badge variant="secondary">Inactive</Badge>,
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => <SortableHeader column={column} label="Created" />,
      cell: ({ row }) => formatDate(row.original.createdAt),
    },
  ], [])

  const affiliateColumns = useMemo<ColumnDef<AdminAffiliate, any>[]>(() => [
    {
      accessorKey: 'code',
      header: ({ column }) => <SortableHeader column={column} label="Code" />,
    },
    {
      id: 'account',
      header: 'Account',
      cell: ({ row }) => row.original.accountEmail ?? row.original.accountId,
    },
    {
      id: 'rewards',
      header: 'Flat fees',
      cell: ({ row }) => <RewardsSummary rewards={row.original.rewards} mode="affiliate" />,
    },
    {
      id: 'share',
      header: 'Share',
      cell: ({ row }) => `${(Number(row.original.sharePercentage) * 100).toFixed(1)}%`,
    },
    {
      accessorKey: 'revenueShareMonths',
      header: 'Months',
    },
    {
      id: 'earned',
      header: 'Earned',
      cell: ({ row }) => <CurrencyDisplay amount={row.original.totalEarnedCents ?? 0} currency={row.original.accountCurrency} inCents hideSign />,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => <Button size="sm" variant="outline" onClick={() => setSelectedAffiliate(row.original)}>Edit</Button>,
    },
  ], [])

  const campaignColumns = useMemo<ColumnDef<AdminReferralCampaign, any>[]>(() => [
    {
      accessorKey: 'code',
      header: ({ column }) => <SortableHeader column={column} label="Code" />,
    },
    {
      accessorKey: 'name',
      header: ({ column }) => <SortableHeader column={column} label="Name" />,
    },
    {
      id: 'rewards',
      header: 'Rewards',
      cell: ({ row }) => <RewardsSummary rewards={row.original.rewards} mode="campaign" />,
    },
    {
      id: 'usage',
      header: 'Usage',
      cell: ({ row }) => `${row.original.usesCount}${row.original.maxUses ? ` / ${row.original.maxUses}` : ''}`,
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => row.original.activeNow ? <Badge>Active</Badge> : <Badge variant="secondary">Inactive</Badge>,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setSelectedCampaign(row.original)}>Edit</Button>
          <Button size="sm" variant="outline" onClick={() => deleteCampaign.mutate(row.original.id)}>Deactivate</Button>
        </div>
      ),
    },
  ], [deleteCampaign])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Referrals</h1>
        <p className="text-muted-foreground">Manage the active referral programme, affiliate accounts, and campaigns.</p>
      </div>

      <Tabs defaultValue="programme" className="space-y-6">
        <TabsList>
          <TabsTrigger value="programme">Referral Programme</TabsTrigger>
          <TabsTrigger value="affiliates">Active Affiliates</TabsTrigger>
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
        </TabsList>

        <TabsContent value="programme" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Active programme</CardTitle>
              <CardDescription>Updating this creates a new programme entry and keeps the old one in history.</CardDescription>
            </CardHeader>
            <CardContent>
              {activeProgramme && (
                <div className="mb-6 rounded-lg border p-4">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <div className="font-medium">Currently active rewards</div>
                    <Badge variant="outline">{activeProgramme.rewards.length} currencies</Badge>
                  </div>
                  <RewardsSummary rewards={activeProgramme.rewards} mode="programme" />
                </div>
              )}

              <form
                className="grid gap-4"
                onSubmit={(e) => {
                  e.preventDefault()
                  createProgramme.mutate({
                    rewards: buildProgrammeRewards(programmeRewards),
                  })
                }}
              >
                <ProgrammeRewardsEditor value={programmeRewards} onChange={setProgrammeRewards} />
                <div className="flex items-end">
                  <Button type="submit" disabled={createProgramme.isPending}>Save programme</Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <PaginatedTableSection
            title="Programme history"
            isLoading={isLoadingProgrammes}
            columns={programmeColumns}
            data={programmeItems}
            pageIndex={programmePage}
            pageSize={programmePageSize}
            totalItems={programmesData?.totalItems ?? 0}
            onPageChange={setProgrammePage}
          />
        </TabsContent>

        <TabsContent value="affiliates" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Create affiliate</CardTitle>
              <CardDescription>Enable an affiliate code directly for an existing account.</CardDescription>
            </CardHeader>
            <CardContent>
              <form
                className="grid gap-4"
                onSubmit={(e) => {
                  e.preventDefault()
                  createAffiliate.mutate({
                    id: newAffiliateAccountId,
                    code: affiliateCode,
                    sharePercentage: Number(affiliateShare),
                    revenueShareMonths: Number(affiliateMonths),
                    rewards: buildAffiliateRewards(affiliateRewards),
                  }, {
                    onSuccess: () => {
                      setNewAffiliateAccountId('')
                      resetAffiliateFields()
                    },
                  })
                }}
              >
                <FormField label="Account ID">
                  <Input value={newAffiliateAccountId} onChange={(e) => setNewAffiliateAccountId(e.target.value)} required />
                </FormField>
                <FormField label="Code">
                  <Input value={affiliateCode} onChange={(e) => setAffiliateCode(e.target.value)} required />
                </FormField>
                <AffiliateRewardsEditor value={affiliateRewards} onChange={setAffiliateRewards} />
                <FormField label="Share percentage">
                  <Input value={affiliateShare} onChange={(e) => setAffiliateShare(e.target.value)} required />
                </FormField>
                <FormField label="Revenue share months">
                  <Input value={affiliateMonths} onChange={(e) => setAffiliateMonths(e.target.value)} required />
                </FormField>
                <div className="flex items-end">
                  <Button type="submit" disabled={createAffiliate.isPending}>Create affiliate</Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <PaginatedTableSection
            title="Active affiliates"
            isLoading={isLoadingAffiliates}
            columns={affiliateColumns}
            data={affiliateItems}
            pageIndex={affiliatePage}
            pageSize={affiliatePageSize}
            totalItems={affiliatesData?.totalItems ?? 0}
            onPageChange={setAffiliatePage}
          />
        </TabsContent>

        <TabsContent value="campaigns" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Create campaign</CardTitle>
            </CardHeader>
            <CardContent>
              <form
                className="grid gap-4"
                onSubmit={(e) => {
                  e.preventDefault()
                    createCampaign.mutate({
                    code: campaignCode,
                    name: campaignName,
                    description: campaignDescription || undefined,
                    rewards: buildCampaignRewards(campaignRewards),
                    maxUses: optionalInteger(campaignMaxUses),
                    activeFrom: campaignActiveFrom ? new Date(campaignActiveFrom).toISOString() : new Date().toISOString(),
                    activeUntil: campaignActiveUntil ? new Date(campaignActiveUntil).toISOString() : null,
                  })
                }}
              >
                <FormField label="Code">
                  <Input value={campaignCode} onChange={(e) => setCampaignCode(e.target.value)} required />
                </FormField>
                <FormField label="Name">
                  <Input value={campaignName} onChange={(e) => setCampaignName(e.target.value)} required />
                </FormField>
                <CampaignRewardsEditor value={campaignRewards} onChange={setCampaignRewards} />
                <FormField label="Max uses">
                  <Input value={campaignMaxUses} onChange={(e) => setCampaignMaxUses(e.target.value)} placeholder="Optional" />
                </FormField>
                <FormField label="Active from">
                  <Input type="datetime-local" value={campaignActiveFrom} onChange={(e) => setCampaignActiveFrom(e.target.value)} />
                </FormField>
                <FormField label="Active until">
                  <Input type="datetime-local" value={campaignActiveUntil} onChange={(e) => setCampaignActiveUntil(e.target.value)} />
                </FormField>
                <div className="md:col-span-3">
                  <FormField label="Description">
                    <Textarea value={campaignDescription} onChange={(e) => setCampaignDescription(e.target.value)} rows={3} />
                  </FormField>
                </div>
                <div className="flex items-end">
                  <Button type="submit" disabled={createCampaign.isPending}>Create campaign</Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <PaginatedTableSection
            title="Campaigns"
            isLoading={isLoadingCampaigns}
            columns={campaignColumns}
            data={campaignItems}
            pageIndex={campaignPage}
            pageSize={campaignPageSize}
            totalItems={campaignsData?.totalItems ?? 0}
            onPageChange={setCampaignPage}
          />
        </TabsContent>

      </Tabs>

      <Dialog open={!!selectedAffiliate} onOpenChange={(open) => {
        if (!open) {
          setSelectedAffiliate(null)
          resetAffiliateFields()
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit affiliate</DialogTitle>
            <DialogDescription>Update the code and reward structure for this affiliate.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <FormField label="Code">
              <Input value={affiliateCode} onChange={(e) => setAffiliateCode(e.target.value)} />
            </FormField>
            <AffiliateRewardsEditor value={affiliateRewards} onChange={setAffiliateRewards} />
            <FormField label="Share percentage">
              <Input value={affiliateShare} onChange={(e) => setAffiliateShare(e.target.value)} />
            </FormField>
            <FormField label="Revenue share months">
              <Input value={affiliateMonths} onChange={(e) => setAffiliateMonths(e.target.value)} />
            </FormField>
          </div>

          <DialogActions
            onCancel={() => {
              setSelectedAffiliate(null)
              resetAffiliateFields()
            }}
            onSave={() => {
              if (!selectedAffiliate) return

              const payload = {
                id: selectedAffiliate.id,
                code: affiliateCode,
                sharePercentage: Number(affiliateShare),
                revenueShareMonths: Number(affiliateMonths),
                rewards: buildAffiliateRewards(affiliateRewards),
              }

              updateAffiliate.mutate(payload, {
                onSuccess: () => {
                  setSelectedAffiliate(null)
                  resetAffiliateFields()
                },
              })
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedCampaign} onOpenChange={(open) => !open && setSelectedCampaign(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit campaign</DialogTitle>
            <DialogDescription>Update the campaign code, reward, schedule, and usage cap.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <FormField label="Code">
              <Input value={campaignCode} onChange={(e) => setCampaignCode(e.target.value)} />
            </FormField>
            <FormField label="Name">
              <Input value={campaignName} onChange={(e) => setCampaignName(e.target.value)} />
            </FormField>
            <CampaignRewardsEditor value={campaignRewards} onChange={setCampaignRewards} />
            <FormField label="Max uses">
              <Input value={campaignMaxUses} onChange={(e) => setCampaignMaxUses(e.target.value)} placeholder="Optional" />
            </FormField>
            <FormField label="Active from">
              <Input type="datetime-local" value={campaignActiveFrom} onChange={(e) => setCampaignActiveFrom(e.target.value)} />
            </FormField>
            <FormField label="Active until">
              <Input type="datetime-local" value={campaignActiveUntil} onChange={(e) => setCampaignActiveUntil(e.target.value)} />
            </FormField>
            <FormField label="Description">
              <Textarea value={campaignDescription} onChange={(e) => setCampaignDescription(e.target.value)} rows={3} />
            </FormField>
          </div>

          <DialogActions
            onCancel={() => setSelectedCampaign(null)}
            onSave={() => {
              if (!selectedCampaign) return

              updateCampaign.mutate({
                id: selectedCampaign.id,
                code: campaignCode,
                name: campaignName,
                description: campaignDescription || undefined,
                rewards: buildCampaignRewards(campaignRewards),
                maxUses: optionalInteger(campaignMaxUses),
                activeFrom: campaignActiveFrom ? new Date(campaignActiveFrom).toISOString() : selectedCampaign.activeFrom,
                activeUntil: campaignActiveUntil ? new Date(campaignActiveUntil).toISOString() : null,
              }, { onSuccess: () => setSelectedCampaign(null) })
            }}
          />
        </DialogContent>
      </Dialog>

    </div>
  )
}

function FormField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-2">
      <Label>{label}</Label>
      {children}
    </div>
  )
}

function ProgrammeRewardsEditor({ value, onChange }: { value: Record<CurrencyCode, { referrer: string; referee: string }>; onChange: (value: Record<CurrencyCode, { referrer: string; referee: string }>) => void }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {currencyCodes().map((currency) => (
        <Card key={currency} variant="outline">
          <CardHeader>
            <CardTitle className="text-base">{currency}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            <FormField label="Referrer credit (cents)">
              <Input value={value[currency].referrer} onChange={(e) => onChange({ ...value, [currency]: { ...value[currency], referrer: e.target.value } })} required />
            </FormField>
            <FormField label="Referee credit (cents)">
              <Input value={value[currency].referee} onChange={(e) => onChange({ ...value, [currency]: { ...value[currency], referee: e.target.value } })} required />
            </FormField>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

function AffiliateRewardsEditor({ value, onChange }: { value: Record<CurrencyCode, string>; onChange: (value: Record<CurrencyCode, string>) => void }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {currencyCodes().map((currency) => (
        <FormField key={currency} label={`Flat fee (${currency})`}>
          <Input value={value[currency]} onChange={(e) => onChange({ ...value, [currency]: e.target.value })} required />
        </FormField>
      ))}
    </div>
  )
}

function CampaignRewardsEditor({ value, onChange }: { value: Record<CurrencyCode, string>; onChange: (value: Record<CurrencyCode, string>) => void }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {currencyCodes().map((currency) => (
        <FormField key={currency} label={`Referee credit (${currency})`}>
          <Input value={value[currency]} onChange={(e) => onChange({ ...value, [currency]: e.target.value })} required />
        </FormField>
      ))}
    </div>
  )
}

function RewardsSummary({ rewards, mode }: { rewards: ReferralReward[]; mode: 'programme' | 'affiliate' | 'campaign' }) {
  return (
    <div className="space-y-1 text-sm">
      {rewards.map((reward) => (
        <div key={reward.currency} className="flex items-center justify-between gap-3">
          <Badge variant="outline">{reward.currency}</Badge>
          <span className="text-right">
            {mode === 'programme' && <>{formatRewardValue(reward.referrerCreditCents, reward.currency)} / {formatRewardValue(reward.refereeCreditCents, reward.currency)}</>}
            {mode === 'affiliate' && <>{formatRewardValue(reward.flatFeeCents, reward.currency)}</>}
            {mode === 'campaign' && <>{formatRewardValue(reward.refereeCreditCents, reward.currency)}</>}
          </span>
        </div>
      ))}
    </div>
  )
}

function DataSection({ title, children, isLoading }: { title: string; children: ReactNode; isLoading: boolean }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? <Skeleton className="h-96 w-full" /> : children}
      </CardContent>
    </Card>
  )
}

function PaginatedTableSection<TData>({
  title,
  isLoading,
  columns,
  data,
  pageIndex,
  pageSize,
  totalItems,
  onPageChange,
}: {
  title: string
  isLoading: boolean
  columns: ColumnDef<TData, any>[]
  data: TData[]
  pageIndex: number
  pageSize: number
  totalItems: number
  onPageChange: (page: number) => void
}) {
  return (
    <DataSection title={title} isLoading={isLoading}>
      <DataTable
        columns={columns}
        data={data}
        enableSearch={false}
        manualPagination={{
          pageIndex,
          pageSize,
          totalItems,
          onPageChange,
          isLoading,
        }}
      />
    </DataSection>
  )
}

function DialogActions({ onCancel, onSave }: { onCancel: () => void; onSave: () => void }) {
  return (
    <DialogFooter>
      <Button variant="outline" onClick={onCancel}>Cancel</Button>
      <Button onClick={onSave}>Save</Button>
    </DialogFooter>
  )
}

function formatDate(value?: string | null) {
  return value ? new Date(value).toLocaleString() : '-'
}

function optionalInteger(value: string) {
  const trimmed = value.trim()
  return trimmed === '' ? null : Number(trimmed)
}

function currencyCodes(): CurrencyCode[] {
  return Object.values(CURRENCIES).map((currency) => toCurrencyCode(currency.code))
}

function buildProgrammeRewards(value: Record<CurrencyCode, { referrer: string; referee: string }>) {
  return currencyCodes()
    .map((currency) => {
      const referrerCreditCents = parseInteger(value[currency]?.referrer)
      const refereeCreditCents = parseInteger(value[currency]?.referee)
      if (referrerCreditCents == null || refereeCreditCents == null) return null

      return {
        currency,
        referrerCreditCents,
        refereeCreditCents,
      }
    })
    .filter((reward): reward is { currency: CurrencyCode; referrerCreditCents: number; refereeCreditCents: number } => reward !== null)
}

function programmeRewardInputs(rewards: ReferralReward[]): Record<CurrencyCode, { referrer: string; referee: string }> {
  return {
    GBP: {
      referrer: String(rewards.find((reward) => reward.currency === 'GBP')?.referrerCreditCents ?? ''),
      referee: String(rewards.find((reward) => reward.currency === 'GBP')?.refereeCreditCents ?? ''),
    },
    USD: {
      referrer: String(rewards.find((reward) => reward.currency === 'USD')?.referrerCreditCents ?? ''),
      referee: String(rewards.find((reward) => reward.currency === 'USD')?.refereeCreditCents ?? ''),
    },
  }
}

function buildAffiliateRewards(value: Record<CurrencyCode, string>) {
  return currencyCodes()
    .map((currency) => {
      const flatFeeCents = parseInteger(value[currency])
      if (flatFeeCents == null) return null

      return {
        currency,
        flatFeeCents,
      }
    })
    .filter((reward): reward is { currency: CurrencyCode; flatFeeCents: number } => reward !== null)
}

function buildCampaignRewards(value: Record<CurrencyCode, string>) {
  return currencyCodes()
    .map((currency) => {
      const refereeCreditCents = parseInteger(value[currency])
      if (refereeCreditCents == null) return null

      return {
        currency,
        refereeCreditCents,
      }
    })
    .filter((reward): reward is { currency: CurrencyCode; refereeCreditCents: number } => reward !== null)
}

function rewardInputs(rewards: ReferralReward[], field: 'flatFeeCents' | 'refereeCreditCents'): Record<CurrencyCode, string> {
  return {
    GBP: String(rewards.find((reward) => reward.currency === 'GBP')?.[field] ?? ''),
    USD: String(rewards.find((reward) => reward.currency === 'USD')?.[field] ?? ''),
  }
}

function formatRewardValue(amount: number | null | undefined, currency: string) {
  if (amount == null) return '-'
  return <CurrencyDisplay amount={amount} currency={currency} inCents hideSign />
}

function parseInteger(value?: string | null) {
  const trimmed = value?.trim() ?? ''
  if (trimmed === '') return null

  const parsed = Number(trimmed)
  return Number.isInteger(parsed) ? parsed : null
}

function toCurrencyCode(value?: string | null): CurrencyCode {
  if (!value) return 'GBP'

  const normalized = value.toUpperCase()
  return normalized in CURRENCIES ? normalized as CurrencyCode : 'GBP'
}

function toLocalDateTimeInputValue(value?: string | null) {
  if (!value) return ''

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  const hours = `${date.getHours()}`.padStart(2, '0')
  const minutes = `${date.getMinutes()}`.padStart(2, '0')

  return `${year}-${month}-${day}T${hours}:${minutes}`
}
